import React, { useEffect, useRef } from "react";
import { Row, Col, Button } from "antd";
import { gsap } from "gsap";
import { SendOutlined } from "@ant-design/icons";
import { SiHey } from "react-icons/si";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import backgroundbit from "../assets/background-component.png";
import tshirtImg from "../assets/right_side.png";

const Banner = () => {
  const bannerRef = useRef(null);
  gsap.registerPlugin(ScrollTrigger);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // ✅ Hero text animation
      gsap.from(".text-block", {
        scrollTrigger: {
          trigger: ".text-block",
          start: "top 85%",
          end: "top 40%",
          toggleActions: "play none none reverse",
        },
        y: 50,
        opacity: 0,
        duration: 1.5,
        ease: "power3.out",
        stagger: 0.2,
      });

      // ✅ T-shirt image animation
      gsap.from(".image-block img", {
        scrollTrigger: {
          trigger: ".image-block",
          start: "top 90%",
          end: "top 40%",
          toggleActions: "play none none reverse",
        },
        x: 120,
        opacity: 0,
        duration: 1.5,
        ease: "power3.out",
      });

      // ✅ Animated heading scaling
      gsap.from(".animated-text", {
        scrollTrigger: {
          trigger: ".animated-text",
          start: "top 85%",
          end: "top 30%",
          scrub: true,
        },
        scale: 0.8,
        opacity: 0.5,
        ease: "power2.out",
      });

      // ✅ Rotating word bounce
      gsap.fromTo(
        ".rotating-word",
        { rotation: -75, y: -80, opacity: 0 },
        {
          scrollTrigger: {
            trigger: ".animated-text",
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
          rotation: 0,
          y: 0,
          opacity: 1,
          ease: "bounce.out",
          duration: 1.8,
        }
      );

      // ✅ Social links
      gsap.from(".social-links li", {
        scrollTrigger: {
          trigger: ".social-links",
          start: "top 95%",
        },
        y: 40,
        opacity: 0,
        duration: 1,
        stagger: 0.3,
        ease: "back.out(1.7)",
      });

      // ✅ Background decoration
      gsap.from(".animate-slideDown", {
        scrollTrigger: {
          trigger: ".banner-short",
          start: "top 100%",
          toggleActions: "play none none reverse",
        },
        x: -100,
        opacity: 0,
        duration: 1.5,
        ease: "power2.out",
      });
    }, bannerRef);

    return () => ctx.revert();
  }, []);

  return (
    <div
      ref={bannerRef}
      className="banner-short overflow-hidden relative min-h-screen bg-white"
    >
      <div className="container mx-auto px-4 py-16">
        <Row gutter={16} align="middle">
          {/* LEFT SECTION */}
          <Col xs={24} md={12}>
            <div className="text-block">
              <p className="text-sm font-medium text-[23px] text-black mb-2 font-custom image-block">
                <span className="text-[#6cc5bf] text-4xl">_</span> Design, Create,
                And Wear Your Story
              </p>
              <h1 className="text-4xl md:text-7xl font-bold text-black font-custom animated-text image-block leading-snug md:leading-[1.3]">
                Bring Your Ideas{" "}
                <span className="text-[#6cc5bf] rotating-word inline-block pr-3">
                  To Life{" "}
                </span>
                On Custom <br />
                <span>T-Shirts</span>
              </h1>
              <p className="font-outfit w-[80%] text-gray-700 mt-4 text-lg font-light image-block">
                Your T-Shirt Is More Than Just Fabric — It’s A Canvas For Your
                Ideas, Your Brand, And Your Story. At Initial Global Trade, We
                Make It Easy To Create Custom T-Shirts That Stand Out With Bold
                Colors, Durable Prints, And Endless Design Possibilities.
              </p>
              <Button
                className="relative font-custom text-lg font-semibold
                  overflow-hidden border-2 border-teal-400 rounded-full
                  px-6 h-[45px] transition-all duration-300 ease-in-out
                  text-teal-600 bg-white hover:!bg-[#6cc5bf] hover:!border-[#000]
                  hover:!text-white shadow-[6px_6px_0_0_#14b8a6]
                  hover:shadow-[0px_0px_0_0_#14b8a6]
                  active:shadow-[0_2px_0_0_#14b8a6]
                  active:translate-y-[4px] group"
                type="default"
                icon={<SendOutlined />}
                style={{
                  marginTop: 24,
                  borderRadius: "30px",
                  padding: "0 24px",
                  height: "45px",
                }}
              >
                Let’s Talk — Send A Message <SiHey />
              </Button>
            </div>
          </Col>

          {/* RIGHT SECTION */}
          <Col xs={24} md={12}>
            <div className="image-block relative flex justify-center items-center">
              <img
                src={tshirtImg}
                alt="Custom T-shirt"
                style={{ maxWidth: "100%", height: "auto" }}
              />
            </div>
          </Col>
        </Row>
      </div>

      {/* Decorative background */}
      <img
        src={backgroundbit}
        alt="decor"
        className="absolute bottom-20 left-0 w-40 "
      />

      {/* Rotated Email */}
      <div className="main-gmail social-links">
        <a
          href="mailto:initialglobaltrade@gmail.com"
          className="font-outfit text-lg tracking-wider 
        absolute top-[140px] -right-14 rotate-[-90deg]
        bg-[#6cc5bf] text-white px-3 py-1 rounded-md shadow-md  duration-300 "
        >
          initialglobaltrade@gmail.com
        </a>

      </div>
      {/* Social Links */}
      <ul
        className="social-links font-outfit text-lg absolute top-[550px] -right-14 
        rotate-[-90deg] flex items-center justify-center gap-8"
      >
        <li>
          <a href="/" className="hover:text-[#6cc5bf] transition-colors duration-300">
            Instagram
          </a>
        </li>
        <li>
          <a href="/" className="hover:text-[#6cc5bf] transition-colors duration-300">
            Facebook
          </a>
        </li>
        <li>
          <a href="/" className="hover:text-[#6cc5bf] transition-colors duration-300">
            Twitter
          </a>
        </li>
      </ul>
    </div>
  );
};

export default Banner;
